import React, { useState, useRef, useEffect} from 'react';
import Form from 'react-bootstrap/Form';
import 'bootstrap/dist/css/bootstrap.css';

import Stats from './Stats';
import TimeSeries from './TimeSeries';
import {toggleChat, toggleGraphView, hasMeetingEnded} from './organizer_options';
import {firestore, timestamp} from './firebaseConfig';
import {activeAttendees, getAvgMessages, getMaxReaction} from './graph_display';
import { useSessionReactions, useSessionColors } from './reusableHooks';
import Piechart from './Piechart';

//Checks the number of attendees (live)
export const useMeetingEnded = (sessionId) => {
    const [meetingEnded,setMeetingEnded] = useState(null);
    const mountedRef = useRef(false);
  
    useEffect(() => {
      mountedRef.current = true;
      if(sessionId){
        const unsubscribe = firestore
            .collection('meetings/').doc(sessionId)
            .onSnapshot((doc) => {
                if(mountedRef.current) setMeetingEnded(doc.data()?.endTime !== null);
            });
        return () => {
          unsubscribe();  
          mountedRef.current = false; 
        } 
      } 
    }, [sessionId]) 
    
    return meetingEnded;
}

//Checks the number of attendees (live)
export const useNumAttendees = (sessionId) => {
    const [attendeeNum,setNum] = useState(null);
    const mountedRef = useRef(false);
  
    useEffect(() => {
      mountedRef.current = true;
      if(sessionId){
        const unsubscribe = firestore
            .collection('attendees')
            .where("sessionId", "==", sessionId)
            .onSnapshot((doc) => {
                if(mountedRef.current) setNum(doc.size);
            });
        return () => {
          unsubscribe();  
          mountedRef.current = false; 
        } 
      } 
    }, [sessionId]) 
    
    return attendeeNum
}

//Checks the active number of attendees (live)
export const useActivePar = (sessionId, interval, meetingEnded) => {
    const [active,setActive] = useState(null);
    const mountedRef = useRef(false);
  
    useEffect(() => {
      mountedRef.current = true;
      if(sessionId && interval && meetingEnded === false){
        const unsubscribe = firestore
            .collection("meetings/" + sessionId + "/votes")
            .onSnapshot((doc) => { //is called everytime the collection changes
            
                if(mountedRef.current) activeAttendees(sessionId, interval).then(num => setActive(num));
            });
        return () => {
          unsubscribe();  
          mountedRef.current = false; 
        } 
      } 
      else if(sessionId && meetingEnded){
        const unsubscribe = firestore
            .collection("meetings/" + sessionId + "/votes")
            .onSnapshot((doc) => { //is called everytime the collection changes
                if(mountedRef.current) activeAttendees(sessionId, null).then(num => setActive(num));
            });
        return () => {
          unsubscribe();  
          mountedRef.current = false; 
        } 
      } 
    }, [sessionId, interval, meetingEnded]) 
    
    return active
}

const useNumReacts = (sessionId) => {
    const mountedRef = useRef(false);
    const [clicks, setClicks] = useState(0);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    },[])

    useEffect(() => {
        if(sessionId) {
            const unsubscribe_clicks = firestore
            .collection("meetings/" + sessionId + "/voteCount").orderBy("reaction")
                .onSnapshot((snapshot) => {
                    let aggregate = 0;
                    snapshot.docs.map((doc) => {{
                        aggregate += doc.data().count
                    }})
                    if(mountedRef.current) setClicks(aggregate)
                });
    
            return () => {
                unsubscribe_clicks();  
            }   
        }
    }, [sessionId]);

    return clicks;
}
//Checks the number of interactions over last minute (live), avg interactions
export const useNumInteracts = (sessionId, interval, meetingEnded) => {
    const mountedRef = useRef(false);
    const [clicks, setClicks] = useState(0);
    const [messages, setMessages] = useState(0);
    const [interacts,setInteracts] = useState(0);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    },[])
    
    useEffect(() => {
      if(sessionId && interval && !meetingEnded){
        const unsubscribe_clicks = firestore
        .collection("meetings/" + sessionId + "/votes").where("timestamp", ">=", interval)
            .onSnapshot((snapshot) => {
                if(mountedRef.current) setClicks(snapshot.size);
            });

        const unsubscribe_messages = firestore
        .collection("meetings/" + sessionId + "/messages").where("timestamp", ">=", interval)
            .onSnapshot((snapshot) => {
                if(mountedRef.current) setMessages(snapshot.size);
            });

        return () => {
            unsubscribe_clicks();  
            unsubscribe_messages();
        } 
      }
    }, [sessionId, interval]) 

    useEffect(() => {
        if(mountedRef.current) setInteracts(clicks + messages);
    }, [clicks, messages])

    useEffect(() => {
        if(mountedRef.current && sessionId && meetingEnded) getAvgMessages(sessionId).then((avg) => setInteracts(avg));
    }, [sessionId, meetingEnded])

    return interacts
}

function NumInteractions({sessionId, meetingEnded, total}){
    const mountedRef = useRef(false);

    const LAST_MINUTE = () => (timestamp.fromMillis((timestamp.now().seconds*1000)-60000));
    const [interval, setNewInterval] = useState(LAST_MINUTE());

    //refreshes component every 60 seconds
    useEffect(() => {
        mountedRef.current = true;
        const timer = setInterval(() => {
            if(mountedRef.current) setNewInterval(LAST_MINUTE());
          return () => clearInterval(timer)
        }, 60000);
        return () => { mountedRef.current = false; };
    },[])
    
    const num_interacts = useNumInteracts(sessionId, interval, meetingEnded);
    const num_reacts = useNumReacts(sessionId);

    return (
        <>
        {!total? 
            <div className="messagesPerSec">
                <i className="grid-icon far fa-comment"></i>
                <div className="grid-label">
                    <h4>{num_interacts}</h4><h6 className="text-muted">
                        {!meetingEnded? "Interactions/min" : "Average Interactions/min"}
                        </h6>
                </div>
            </div> 
            :
            <div className="num-reactions">
                <i className="grid-icon far fa-comment"></i>
                <div className="grid-label">
                    <h4>{num_reacts}</h4><h6 className="text-muted">
                        Total Reactions
                        </h6>
                </div>
            </div> 
        }
        </>
    )
}

function TrendingReaction({sessionId, trendingReaction, meetingEnded, setTrendingReaction}) {
    const mountedRef = useRef(false);
    //const barColors = ["#E41A1C", "#FF7F00", "#377EB8", "#984EA3", "#4DAF4A", "#404142"];
    const barColors = useSessionColors(sessionId, false);
    
    //fail safe if there is no color array in firebase
    //should manually update past meetings
    
    const COLORS = {};
    const participantTags = useSessionReactions(sessionId, false);
    participantTags?.map((reaction, index) => {
      COLORS[reaction] = barColors[index % barColors.length];
    })

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    },[])

    useEffect(() => {
        if(sessionId && meetingEnded && mountedRef.current) { //meetingEnded by default so this will always call
            getMaxReaction(sessionId).then((reaction) => setTrendingReaction(reaction));
        }

    },[sessionId, meetingEnded])

    return (
    <div className="trending">
        <i className="grid-icon fas fa-chart-line"></i>
        <div className="grid-label">
            {trendingReaction && trendingReaction !== "None" && !meetingEnded && <><h4 style={{color: COLORS[trendingReaction]}}>{trendingReaction}</h4><h6 className="text-muted">Trending Reaction</h6></>}
            {trendingReaction === "None" && !meetingEnded && <><h4 className="">None</h4><h6 className="text-muted">Trending Reaction</h6></>}
            {trendingReaction === null && !meetingEnded && <><h4 className="invisible">-</h4><h6 className="text-muted">Trending Reaction</h6></>}
            {trendingReaction && trendingReaction !== "None" && meetingEnded && <><h4 style={{color: COLORS[trendingReaction]}}>{trendingReaction}</h4><h6 className="text-muted">All-time Top Reaction</h6></>}
            {trendingReaction === "None" && meetingEnded && <><h4 className="">None</h4><h6 className="text-muted">All-time Top Reaction</h6></>}
            {trendingReaction === null && meetingEnded && <><h4 className="invisible">-</h4><h6 className="text-muted">All-time Top Reaction</h6></>}
        </div>
    </div>
    )
}

function ActiveParticipants({sessionId, num_attendees, meetingEnded}) {
    const mountedRef = useRef(false);

    const LAST_MINUTE = () => (timestamp.fromMillis((timestamp.now().seconds*1000)-60000));
    const [interval, setNewInterval] = useState(LAST_MINUTE());

    const active_participants = useActivePar(sessionId, interval, meetingEnded);

    //refreshes component every 60 seconds
    useEffect(() => {
        mountedRef.current = true;
        const timer = setInterval(() => {
            if(mountedRef.current) setNewInterval(LAST_MINUTE());
            return () => clearInterval(timer)
        }, 60000);
        return () => { mountedRef.current = false; };
    },[])

    return (
        <div className="active-participants">
            <i className="grid-icon far fa-hand-paper"></i>
            <div className="grid-label">
                {active_participants > -1 && <h4>{active_participants}</h4>}
                {active_participants === null && <h4 className="invisible">-</h4>}
                {meetingEnded === false && <h6 className="text-muted">Active participants (last minute)</h6>}
                {meetingEnded && <h6 className="text-muted">Active participants (all-time)</h6>}
            </div>
        </div>
    )
}

export default function Dashboard({sessionId, sessionChatOn, sessionGraphOn, windowSize}) {
    //let dClass = inView? "" : "d-none";
    //const [showDashboard, setShow] = useState(dClass);
    const num_attendees = useNumAttendees(sessionId);
    const [chatStatus, setChatStatus] = useState(sessionChatOn);
    const [graphStatus, setGraphStatus] = useState(sessionGraphOn);
    const meetingEnded = useMeetingEnded(sessionId);
    const [trendingReaction, setTrendingReaction] = useState(null);
    //if(sessionId) hasMeetingEnded(sessionId).then((response) => setMeetingEnded(response));

    const [graphType, setGraphType] = useState("total-bar"); 

    const handleCheckChat = (event) => {
        const target = event.target;
        if(target.id === 'chat-public') setChatStatus(1)
        else if(target.id === 'chat-off') setChatStatus(0)
        else setChatStatus(2)
    }

    const handleCheckGraph = (event) => {
        const target = event.target;
        if(target.id === 'graph-on') setGraphStatus(true)
        else if(target.id === 'graph-off') setGraphStatus(false)
        else setGraphStatus(false)
    }

    useEffect(() => {
        toggleChat(sessionId, chatStatus)
    },[sessionId, chatStatus]);

    useEffect(() => {
        toggleGraphView(sessionId, graphStatus)
    },[sessionId, graphStatus]);

    /*useEffect(() => {
        dClass = inView? "" : "d-none";
        setShow(dClass);
    },[sessionId, inView]);*/

    return (
        <>
        <div className={"dashboard-container"}>
            <div className="num-attendees">
                <i className="grid-icon far fa-user"></i>
                <div className="grid-label">
                    <h4>{num_attendees}</h4>
                    <h6 className="text-muted">Attendees</h6>
                </div>
            </div>
            <ActiveParticipants sessionId={sessionId} num_attendees={num_attendees} meetingEnded={meetingEnded}/>
                
            <TrendingReaction sessionId={sessionId} trendingReaction={trendingReaction} meetingEnded={meetingEnded} setTrendingReaction={setTrendingReaction}/>
            
            <NumInteractions sessionId={sessionId} meetingEnded={meetingEnded} total={false}/>
            <NumInteractions sessionId={sessionId} meetingEnded={meetingEnded} total={true}/>
            <Piechart sessionId={sessionId} setTrendingReaction={setTrendingReaction} meetingEnded={meetingEnded}/>
            {/*<div className="sentiment">
                <div className="sentiment-header">
                    <h6 className="mb-1 font-weight-bold">Total Participant Reactions</h6>
                </div>
                <Piechart sessionId={sessionId} setTrendingReaction={setTrendingReaction}/>
                <Stats sessionId={sessionId} isAdmin={true} parent={"dashboard"}/>
            </div>*/}
            <div className="sentiment-overtime">
                <span className="row">
                    <h6 className="col font-weight-bold d-inline align-self-start">Reactions over time</h6>
                    <Form.Control as="select" size="sm" value={graphType} 
                    onChange={e => {setGraphType(e.target.value)}}
                    className="col d-inline h-auto mr-3 py-0">
                        <option value="total-bar">Total Reactions</option>
                        <option value="total-bar-unique">Unique Attendee Reactions</option>
                        <option value="accumulated-bar">Accumulated Bar Graph</option>
                        <option value="accumulated-line">Accumulated Line Graph</option>
                        <option value="interval-line">Interval Line Graph</option>
                        <option value="interval-bar">Interval Bar Graph</option>
                        {/*<option value="interval-radial">Radial Graph - Interval</option>
                        <option value="accumulated-radial">Radial Graph - Accumulated</option>*/}
                    </Form.Control>
                </span>
                {graphType !== "total-bar" && graphType !== "total-bar-unique" && <TimeSeries sessionId={sessionId} type={graphType}/>}
                {graphType === "total-bar" && <Stats sessionId={sessionId} isAdmin={true} parent={"dashboard"} windowSize={windowSize} uniqueOnly={false}/>}
                {graphType === "total-bar-unique" && <Stats sessionId={sessionId} isAdmin={true} parent={"dashboard"} windowSize={windowSize} uniqueOnly={true}/>}
                {/*<BarTimeline sessionId={sessionId} type={"interval-line"}/>*/}
            </div>
            <div className="moderator-tools py-1">
                <h5 className="mod font-weight-bold">Preferences</h5>
                <div className="chatControl">
                        <fieldset>
                            <Form.Group className="m-0">
                                <Form.Label as="legend" column className="px-0">
                                    <h6 className="m-0 font-weight-bold">Chat:</h6>
                                </Form.Label>
                                    <Form.Check
                                    type="radio"
                                    label="Public"
                                    name="chat"
                                    id="chat-public"
                                    checked={chatStatus === 1}
                                    onChange={handleCheckChat}
                                    />
                                    <Form.Check
                                    type="radio"
                                    label="Organizers only"
                                    name="chat"
                                    id="chat-private"
                                    checked={chatStatus === 2}
                                    onChange={handleCheckChat}
                                    />
                                    <Form.Check
                                    type="radio"
                                    label="Disabled"
                                    name="chat"
                                    id="chat-off"
                                    checked={chatStatus === 0}
                                    onChange={handleCheckChat}
                                    />
                            </Form.Group>
                        </fieldset>
                    </div>
                    <div className="graphControl">
                        <fieldset>
                            <Form.Group className="m-0">
                                <Form.Label as="legend" column className="px-0">
                                    <h6 className="m-0 font-weight-bold">Graph:</h6>
                                </Form.Label>
                                    <Form.Check
                                    type="radio"
                                    label="On"
                                    name="graph-control"
                                    id="graph-on"
                                    checked={graphStatus === true}
                                    onChange={handleCheckGraph}
                                    />
                                    <Form.Check
                                    type="radio"
                                    label="Off"
                                    name="graph-control"
                                    id="graph-off"
                                    checked={graphStatus === false}
                                    onChange={handleCheckGraph}
                                    />
                            </Form.Group>
                        </fieldset>
                </div>
            </div>
        </div>
        </>
    )
}
